import { useMemo } from "react";
import { FlatList } from "react-native";
import { useLocalSearchParams } from "expo-router";

import {
  ApplicationStatusEnumType,
  ApplicationTypeEnumType,
} from "@libs/api/patients";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnLink } from "@/components/VnLink";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useListApplicationsQuery } from "@/data/queries/applicationsQueries";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { composeBrandLabel } from "@/utils/brandLabel";
import { formatApplicationDate } from "@/utils/date";

const getStatusLabel = (status: ApplicationStatusEnumType): I18nKeyType => {
  return status === "SUBMITTED"
    ? "native:applicationsListScreen.submitted"
    : "native:applicationsListScreen.inProgress";
};

export const ApplicationsListScreen = () => {
  const { t } = useTranslator();

  const { applicationType } = useLocalSearchParams<{
    applicationType: ApplicationTypeEnumType;
  }>();

  const { data: applications, isLoading } = useListApplicationsQuery({
    filters: { applicationType },
    isEnabled: true,
  });

  const brandLabel = useMemo(() => {
    if (!applications?.length) return applicationType;
    const allBrands = applications.flatMap(a => a.brands);
    return composeBrandLabel(allBrands, applicationType);
  }, [applications, applicationType]);

  const titleText = t("native:applicationsListScreen.title", {
    applicationType: brandLabel,
  });

  if (isLoading) {
    return (
      <ScreenLayout>
        <VnBox className="flex-1 items-center justify-center">
          <VnLoadingSpinner />
        </VnBox>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout variant="LIST">
      <VnBox className="flex-1 gap-5 pt-4">
        <VnText variant="headerTwo">{titleText}</VnText>

        <FlatList
          data={applications}
          keyExtractor={item => item.id}
          contentContainerClassName="gap-4 pb-8"
          renderItem={({ item }) => {
            return (
              <VnLink
                href={{
                  pathname: "/(authed)/application/[applicationIds]/submit",
                  params: { applicationIds: item.id },
                }}
                isDisabled={
                  item.applicationStatus === "SUBMITTED" ||
                  item.applicationStatus === "DRAFT"
                }>
                <VnCard className="gap-2">
                  <VnText
                    variant="lgMedium"
                    i18nKey={getStatusLabel(item.applicationStatus)}
                  />
                  <VnText variant="md" className="text-muted-foreground">
                    {formatApplicationDate(item.createdAt)}
                  </VnText>
                </VnCard>
              </VnLink>
            );
          }}
          ListEmptyComponent={
            <VnText
              variant="md"
              className="text-muted-foreground text-center"
              i18nKey="native:applicationsListScreen.noApplications"
            />
          }
        />
      </VnBox>
    </ScreenLayout>
  );
};
